import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Shield, FileText, Building2, TrendingUp, Clock, Users, Handshake } from 'lucide-react';
import SellerForm from './SellerForm';

const GetStarted = () => {
  const [showForm, setShowForm] = React.useState(false);

  useEffect(() => {
    if (window.location.hash) {
      const targetId = window.location.hash.substring(1);
      console.log(`GetStarted: Found hash in URL: #${targetId}`);

      setTimeout(() => {
        const targetElement = document.getElementById(targetId);

        if (targetElement) {
          console.log(`GetStarted: Found element #${targetId}, scrolling into view`);
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });

          window.dispatchEvent(
            new CustomEvent('forceAnimations', {
              detail: { timestamp: Date.now() },
            })
          );
        } else {
          console.error(`GetStarted: Element #${targetId} not found after delay`);
        }
      }, 800);
    }
  }, []);

  const handleShowForm = () => {
    setShowForm(true);
    setTimeout(() => {
      const formElement = document.getElementById('seller-form');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 300);
  };

  const criteriaItems = [
    {
      icon: <Building2 size={20} />,
      title: 'AI or Software-Led Offer',
      description: 'A product, platform, workflow tool, or digital business with clear utility.',
    },
    {
      icon: <TrendingUp size={20} />,
      title: 'Online Operating Model',
      description: 'The business should be internet-native, software-enabled, or digitally distributed.',
    },
    {
      icon: <Clock size={20} />,
      title: 'Traction Visibility',
      description: 'We look for clear signs of demand, usage, revenue, or customer pull.',
    },
    {
      icon: <TrendingUp size={20} />,
      title: 'Expansion Potential',
      description: 'There should be a believable path to better growth, execution, or product depth.',
    },
    {
      icon: <Users size={20} />,
      title: 'Acquisition Openness',
      description: 'We want founder or operator conversations where a transition is genuinely on the table.',
    },
    {
      icon: <Handshake size={20} />,
      title: 'Operational Clarity',
      description: 'Documented workflows, customer insight, and operating visibility are strong positives.',
    },
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Submit an Overview',
      description: 'Share the basics of your AI SaaS or online business so we can understand the opportunity quickly.',
      icon: <FileText className="text-primary-600" size={24} />,
    },
    {
      number: '02',
      title: 'Initial Review',
      description: 'If the business fits our focus, we will review the submission and reach out for a more direct conversation.',
      icon: <Shield className="text-primary-600" size={24} />,
    },
    {
      number: '03',
      title: 'Next-Step Conversation',
      description: 'From there, we can talk through acquisition interest, founder involvement, and what the next phase could look like.',
      icon: <TrendingUp className="text-primary-600" size={24} />,
    },
  ];

  return (
    <section className="relative min-h-screen flex flex-col justify-center pt-20 overflow-hidden bg-gradient-to-b from-blue-50 via-white to-blue-50 text-gray-800">
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full bg-gradient-to-r from-primary-500/10 to-primary-300/10 blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full bg-gradient-to-r from-secondary-400/10 to-secondary-200/10 blur-3xl animate-float-slow"></div>

        <svg className="absolute inset-0 w-full h-full" xmlns="https://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" style={{ stopColor: 'rgb(59, 130, 246)', stopOpacity: 0.05 }} />
              <stop offset="100%" style={{ stopColor: 'rgb(147, 197, 253)', stopOpacity: 0.05 }} />
            </linearGradient>
          </defs>
          <motion.path
            d="M0,100 C150,200 350,0 500,100 L500,00 L0,0 Z"
            fill="url(#grad1)"
            initial={{ opacity: 0 }}
            animate={{
              opacity: 0.5,
              y: [0, 10, 0],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              repeatType: 'reverse',
            }}
          />
        </svg>
      </div>

      <div className="container mx-auto px-4 md:px-6 z-10 py-16">
        {!showForm ? (
          <>
            <div className="text-center max-w-4xl mx-auto mb-16">
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center py-2 px-5 rounded-full bg-primary-50 text-primary-600 text-sm font-medium mb-6 border border-primary-100 shadow-sm"
              >
                <Handshake size={16} className="mr-2" />
                Acquisition Interest Form
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-tight mb-8 text-gray-800"
              >
                Tell Us About Your{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-primary-500">
                  AI SaaS or Online Business
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto leading-relaxed"
              >
                Use this form if you want to discuss an acquisition, a founder transition, or an AI-related SaaS or
                online-business opportunity. The goal is to understand fit quickly and keep the conversation grounded in
                the actual business.
              </motion.p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="max-w-5xl mx-auto mb-24"
            >
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-gray-800 mb-4">Our Process</h2>
                <p className="text-gray-600 max-w-3xl mx-auto text-lg">
                  A direct and confidential intake flow for AI SaaS and online-business opportunities
                </p>
              </div>

              <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
                <div className="bg-gradient-to-r from-primary-600 to-primary-500 py-6 px-8">
                  <h3 className="text-2xl font-bold text-white">How We Review Opportunities</h3>
                </div>
                <div className="p-8 md:p-12">
                  <div className="space-y-16">
                    {processSteps.map((step, index) => (
                      <motion.div
                        key={index}
                        className="flex flex-col md:flex-row md:items-start"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.7 + index * 0.2 }}
                      >
                        <div className="flex-shrink-0 mr-0 md:mr-8 mb-6 md:mb-0 flex md:block justify-center">
                          <div className="w-16 h-16 rounded-full bg-primary-50 flex items-center justify-center border-2 border-primary-100 shadow-md">
                            <span className="text-primary-700 text-2xl font-bold">{step.number}</span>
                          </div>
                          {index < processSteps.length - 1 && (
                            <div className="hidden md:block w-0.5 h-24 bg-primary-100 mx-auto my-3"></div>
                          )}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-semibold text-gray-800 mb-3 flex items-center">
                            {step.title}
                            <div className="ml-3 w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center shadow-sm">
                              <div className="text-primary-600">{step.icon}</div>
                            </div>
                          </h3>
                          <p className="text-gray-600 text-lg leading-relaxed">{step.description}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              id="acquisition-fit"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.2 }}
              className="max-w-5xl mx-auto mb-20"
            >
              <div className="text-center mb-10">
                <h2 className="text-3xl font-bold text-gray-800 mb-3">What We Look For</h2>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  The kinds of digital businesses and signals that usually create a stronger fit
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {criteriaItems.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 1.3 + index * 0.1 }}
                    className="bg-white shadow-md border border-gray-100 rounded-xl p-6 hover:shadow-lg transition-all duration-300"
                    whileHover={{ y: -3 }}
                  >
                    <div className="bg-primary-50 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                      <div className="text-primary-600">{item.icon}</div>
                    </div>
                    <h3 className="text-lg font-semibold mb-2 text-gray-800">{item.title}</h3>
                    <p className="text-gray-600 text-sm">{item.description}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.6 }}
              className="max-w-4xl mx-auto mb-16"
            >
              <div className="bg-gradient-to-r from-primary-600 to-primary-500 rounded-2xl shadow-xl overflow-hidden">
                <div className="px-8 py-12 md:p-12">
                  <div className="md:flex items-center">
                    <div className="md:w-2/3 mb-8 md:mb-0 md:pr-8">
                      <h2 className="text-3xl font-bold text-white mb-4">Ready to start the conversation?</h2>
                      <p className="text-primary-50 text-lg">
                        Share the business, product, and transition context. We will review the details and respond if
                        there is a fit with our AI SaaS and online-business focus.
                      </p>
                    </div>
                    <div className="md:w-1/3">
                      <motion.button
                        whileHover={{ scale: 1.05, boxShadow: '0 10px 25px -5px rgba(255, 255, 255, 0.2)' }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleShowForm}
                        className="w-full px-8 py-4 rounded-lg bg-white text-primary-600 font-medium transition-all duration-300 hover:bg-gray-50 shadow-lg flex items-center justify-center"
                      >
                        <span>Start The Process</span>
                        <ArrowRight size={18} className="ml-2" />
                      </motion.button>
                      <div className="mt-4 flex items-center justify-center text-primary-50">
                        <Shield size={16} className="mr-2" />
                        <p className="text-sm">Your information is kept strictly confidential</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        ) : (
          <SellerForm onBack={() => setShowForm(false)} />
        )}
      </div>
    </section>
  );
};

export default GetStarted;
